// @ts-nocheck
import { useEffect, useRef } from 'react';
import { Map as OLMap } from 'ol';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Style, Stroke, Circle, Fill, Text } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { useMapStore } from '@/stores/mapStore';

const STATUS_STYLES = {
  PENDING: {
    stroke: '#f59e0b', // amber
    fill: 'rgba(245, 158, 11, 0.1)',
    dash: [8, 6],
  },
  APPROVED: {
    stroke: '#22c55e', // green
    fill: 'rgba(34, 197, 94, 0.08)',
    dash: undefined,
  },
  REJECTED: {
    stroke: '#ef4444', // red
    fill: 'rgba(239, 68, 68, 0.08)',
    dash: undefined,
  },
} as const;

const DIM_OPACITY = 0.3;
const NORMAL_OPACITY = 0.7;
const HIGHLIGHT_OPACITY = 1;

function buildCorrectionStyle(
  status: string,
  isHighlighted: boolean,
  isDimmed: boolean,
  assetType?: string, // ← add param
) {
  const config =
    STATUS_STYLES[status as keyof typeof STATUS_STYLES] ??
    STATUS_STYLES.PENDING;

  const opacity = isDimmed
    ? DIM_OPACITY
    : isHighlighted
      ? HIGHLIGHT_OPACITY
      : NORMAL_OPACITY;

  return new Style({
    stroke: new Stroke({
      color: config.stroke,
      width: isHighlighted ? 3 : 2,
      lineDash: config.dash,
    }),
    fill: new Fill({ color: config.fill }),
    image: new Circle({
      radius: isHighlighted ? 7 : 5,
      fill: new Fill({ color: config.stroke }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
    // ── Add label ──────────────────────────────────
    text: assetType
      ? new Text({
          text: assetType,
          offsetY: -16,
          font: '600 11px "Inter", sans-serif',
          fill: new Fill({ color: config.stroke }),
          stroke: new Stroke({ color: '#ffffff', width: 3 }),
          backgroundFill: new Fill({ color: 'rgba(255,255,255,0.85)' }),
          padding: [2, 4, 1, 4],
          overflow: true,
        })
      : undefined,
    zIndex: isHighlighted ? 10 : 1,
  });
}

interface Props {
  map: OLMap | null;
}

export const CorrectionOverlayLayer = ({ map }: Props) => {
  const sourceRef = useRef<VectorSource | null>(null);
  const layerRef = useRef<VectorLayer | null>(null);

  const { overlayCorrections, highlightedCorrectionId, overlayVisible } =
    useMapStore();

  // ── Create layer once ──────────────────────────────────
  useEffect(() => {
    if (!map) return;

    const source = new VectorSource();
    sourceRef.current = source;

    const layer = new VectorLayer({
      source,
      properties: { type: 'correction-overlay' },
      zIndex: 9998,
    });

    layerRef.current = layer;
    map.addLayer(layer);

    return () => {
      map.removeLayer(layer);
      sourceRef.current = null;
      layerRef.current = null;
    };
  }, [map]);

  // ── Sync corrections to OL source ─────────────────────
  useEffect(() => {
    const source = sourceRef.current;
    if (!source) return;

    source.clear();

    if (!overlayVisible || overlayCorrections.length === 0) return;

    const format = new GeoJSON();

    for (const correction of overlayCorrections) {
      if (!correction.proposedGeom) continue;

      try {
        // proposedGeom could be a Feature or FeatureCollection
        const geojson = correction.proposedGeom;
        const features =
          geojson.type === 'FeatureCollection'
            ? geojson.features
            : [
                geojson.type === 'Feature'
                  ? geojson
                  : { type: 'Feature', geometry: geojson, properties: {} },
              ];

        for (const featureJson of features) {
          const olFeature = format.readFeature(featureJson, {
            featureProjection: 'EPSG:3857',
          });
          // Tag each OL feature with the correction ID and status
          olFeature.set('correctionId', correction.id);
          olFeature.set('correctionStatus', correction.status);
          olFeature.set('assetType', featureJson.properties?.assetType || '');
          source.addFeature(olFeature);
        }
      } catch (err) {
        console.warn('Failed to render correction:', correction.id, err);
      }
    }
  }, [overlayCorrections, overlayVisible]);

  // ── Style based on highlight state ─────────────────────
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    const hasHighlight = !!highlightedCorrectionId;

    layer.setStyle((feature) => {
      const corrId = feature.get('correctionId');
      const status = feature.get('correctionStatus') || 'PENDING';
      const assetType = feature.get('assetType') || ''; // ← read it
      const isHighlighted = corrId === highlightedCorrectionId;
      const isDimmed = hasHighlight && !isHighlighted;

      return buildCorrectionStyle(status, isHighlighted, isDimmed, assetType);
    });
  }, [highlightedCorrectionId]);

  return null;
};
